<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ROUTES } from '@/router/routeNames';
import { useToast } from 'primevue/usetoast';
import api from '@/services/axios';
import PrimaryButton from '@/components/general/PrimaryButton.vue';
import SecondaryButton from '@/components/general/SecondaryButton.vue';
import DangerButton from '@/components/general/DangerButton.vue';
import ConfirmationModal from '@/components/general/ConfirmationModal.vue';
import { nairaSymbol } from '@/constants';

const route = useRoute();
const router = useRouter();
const toast = useToast();

const product = ref(null);
const isLoading = ref(true);
const showDeleteDialog = ref(false);

const productId = computed(() => route.params.id);

const fetchProduct = async () => {
  try {
    isLoading.value = true;
    // Using the UUID from route params to fetch the product
    const response = await api.get(`api/business/products/${productId.value}`);
    product.value = response.data;
  } catch (error) {
    console.error('Error fetching product:', error);

    // Check if it's an unauthorized error
    if (error.response && (error.response.status === 403 ||
        (error.response.data && error.response.data.message === 'This action is unauthorized.'))) {
      toast.add({
        severity: 'error',
        summary: 'Unauthorized',
        detail: 'You are not authorized to view this product.',
        life: 3000
      });
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load product details',
        life: 3000
      });
    }

    // Redirect back to products list
    router.push({ name: ROUTES.PRODUCTS });
  } finally {
    isLoading.value = false;
  }
};

const deleteProduct = async () => {
  try {
    // Using the UUID from route params to delete the product
    await api.delete(`api/business/products/${productId.value}`);
    toast.add({
      severity: 'success',
      summary: 'Product Deleted',
      detail: 'Product has been deleted successfully',
      life: 3000
    });
    mixpanel.track('Delete Product was clicked from Product Page');

    router.push({ name: ROUTES.PRODUCTS });
  } catch (error) {
    console.error('Error deleting product:', error);

    // Check if it's an unauthorized error
    if (error.response && (error.response.status === 403 ||
        (error.response.data && error.response.data.message === 'This action is unauthorized.'))) {
      toast.add({
        severity: 'error',
        summary: 'Unauthorized',
        detail: 'You are not authorized to delete this product.',
        life: 3000
      });

      // Close the dialog
      showDeleteDialog.value = false;
    } else {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete product',
        life: 3000
      });
    }
  }
};

const confirmDelete = () => {
  showDeleteDialog.value = true;
};

const goToEdit = () => {
  router.push({
    name: ROUTES.PRODUCT_EDIT,
    params: { id: productId.value }
  });
};

const goBack = () => {
  router.push({ name: ROUTES.PRODUCTS });
};


onMounted(async () => {
  await fetchProduct();
});


</script>

<template>
    <div class="sm:py-12">
      <div class="sm:px-6 lg:px-8">
        <div class="overflow-hidden bg-white shadow-sm dark:bg-gray-800 sm:rounded-lg">
          <div v-if="isLoading" class="flex justify-center items-center p-12">
            <div class="w-12 h-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
          </div>

          <div v-else-if="product" class="p-6">
            <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
              <!-- Product Image -->
              <div class="flex justify-center">
                <img
                  v-if="product.image_url"
                  :src="product.image_url"
                  :alt="product.name"
                  class="object-contain max-h-96 rounded-lg shadow-md"
                />
                <div
                  v-else
                  class="flex justify-center items-center w-full h-64 bg-gray-200 rounded-lg dark:bg-gray-700"
                >
                  <span class="text-gray-500 dark:text-gray-400">No image available</span>
                </div>
              </div>

              <!-- Product Details -->
              <div class="space-y-6">
                <div>
                  <h1 class="text-3xl font-bold text-gray-900 dark:text-white">{{ product.name }}</h1>
                  <p class="mt-2 text-xl font-semibold text-indigo-600 dark:text-indigo-400">
                    {{ nairaSymbol }}{{ parseFloat(product.price).toFixed() }}
                  </p>
                </div>

                <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h3 class="text-lg font-medium text-gray-900 dark:text-white">Description</h3>
                  <p class="mt-2 text-gray-600 dark:text-gray-300">{{ product.description }}</p>
                </div>

                <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h3 class="text-lg font-medium text-gray-900 dark:text-white">Details</h3>
                  <div class="grid grid-cols-2 gap-y-2 gap-x-4 mt-2">
                    <div class="text-sm text-gray-500 dark:text-gray-400">Stock</div>
                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ product.stock }}</div>

                    <div class="text-sm text-gray-500 dark:text-gray-400">Category</div>
                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ product.category?.name || 'Uncategorized' }}
                    </div>

                    <div class="text-sm text-gray-500 dark:text-gray-400">Sub-Category</div>
                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ product.sub_category?.name || 'None' }}
                    </div>

                    <div class="text-sm text-gray-500 dark:text-gray-400">Created</div>
                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ new Date(product.created_at).toLocaleDateString() }}
                    </div>

                    <div class="text-sm text-gray-500 dark:text-gray-400">Last Updated</div>
                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ new Date(product.updated_at).toLocaleDateString() }}
                    </div>
                  </div>
                </div>

                <div class="flex pt-4 space-x-4 border-t border-gray-200 dark:border-gray-700">
                  <PrimaryButton @click="goToEdit">
                    Edit Product
                  </PrimaryButton>
                  <DangerButton @click="confirmDelete">
                    Delete Product
                  </DangerButton>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="p-6 text-center">
            <p class="text-gray-600 dark:text-gray-400">Product not found</p>
            <SecondaryButton @click="goBack" class="mt-4">
              Back to Products
            </SecondaryButton>
          </div>
        </div>
      </div>

          <!-- Delete Confirmation Dialog -->
          <ConfirmationModal :show="showDeleteDialog" @close="showDeleteDialog = false">
            <template #title>
              Delete Product
            </template>
            <template #content>
              <p>
                Are you sure you want to delete "{{ product?.name }}"? This action cannot be undone.
              </p>
            </template>
            <template #footer>
              <SecondaryButton class="mr-2" @click="showDeleteDialog = false">
                Cancel
              </SecondaryButton>
              <DangerButton @click="deleteProduct">
                Delete
              </DangerButton>
            </template>
          </ConfirmationModal>
    </div>
</template>
